import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getDatabase, ref, onValue, get } from 'firebase/database';

import isNil from 'lodash/isNil';

import { Box, Button, Typography } from '@mui/material';

import { EventAccess, EventFormData } from '../components/data_types/types';
import { toInputEventTime } from '../components/data/formatting';

import AdminWrapper from '../components/layout/AdminWrapper';
import AddEventForm from '../components/forms/AddEventForm';
import { DS } from '../components/shared/Dashboard';

const StatusMessage = ({ title, text, showBack }: { title: string; text?: string; showBack?: boolean }) => (
  <AdminWrapper>
    <Box sx={{ textAlign: 'center', py: 8, background: DS.surface, border: `1px solid ${DS.border}`, borderRadius: DS.radius }}>
      <Typography sx={{ fontWeight: 700, color: DS.text }}>{title}</Typography>
      {text && <Typography sx={{ fontSize: '13px', color: DS.textMuted, mt: 0.5 }}>{text}</Typography>}
      {showBack && (
        <Button component={Link} to="/admin/events" sx={{ mt: 2, borderRadius: '20px', textTransform: 'none', fontWeight: 700 }}>
          Back to events
        </Button>
      )}
    </Box>
  </AdminWrapper>
);

const AdminEditEvent = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState<EventFormData | undefined>(undefined);
  const { eventID } = useParams();

  useEffect(() => {
      const db = getDatabase();
      const eventsRef = ref(db, `/events/pending/${eventID}`);

      onValue(eventsRef, async (snapshot) => {
          const event: EventFormData | null = snapshot.val();

          // An invite event sent back to pending has no draft list any more; start from who is already invited
          if (event && event.eventAccess === EventAccess.Invite && !event.invitedPlayers) {
              try {
                  const roster = await get(ref(db, `/events/inviteAccess/${eventID}`));
                  event.invitedPlayers = Object.keys(roster.val() || {});
              } catch (error) {
                  console.error('Could not load invited players:', error);
              }
          }

          // The date inputs need "YYYY-MM-DDTHH:mm"; Firebase stores "YYYY-MM-DD HH:mm:ss"
          setFormData(event ? {
              ...event,
              eventStartTime: toInputEventTime(event.eventStartTime),
              eventEndTime: toInputEventTime(event.eventEndTime),
          } : undefined);
          setIsLoading(false);
      });
  }, [eventID]);

  if (isLoading) {
      return <StatusMessage title="Loading event data..." />;
  }

  if (isNil(formData)) {
      return (
        <StatusMessage
          title="Could not find an event with that ID"
          text="Only pending events can be edited."
          showBack
        />
      );
  }

  return (
    <AdminWrapper>
      <AddEventForm eventFormData={formData} isEditMode={true} />
    </AdminWrapper>
  );
};

export default AdminEditEvent;
